/**
 * ContactAdminPage.jsx — Formulaire de contact interne (élève ou enseignant → admin)
 * + historique des demandes envoyées et réponses reçues.
 * URL : /eleve/contact-admin  ou  /moniteur/contact-admin (même composant, mode détecté)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Paperclip, Send, CheckCircle2, Clock, AlertCircle, X } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const CATS_ELEVE = [
  { value: 'question', label: 'Question' },
  { value: 'reclamation', label: 'Réclamation' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'autre', label: 'Autre' },
];
const CATS_ENS = [
  { value: 'question_technique', label: 'Question technique' },
  { value: 'difficulte_eleve', label: 'Difficulté avec un élève' },
  { value: 'suggestion_pedagogique', label: 'Suggestion pédagogique' },
  { value: 'autre', label: 'Autre' },
];

export default function ContactAdminPage() {
  const { pathname } = useLocation();
  const isEnseignant = pathname.startsWith('/moniteur');
  const CATS = isEnseignant ? CATS_ENS : CATS_ELEVE;
  const accent = isEnseignant ? 'rose' : 'rose';

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState(CATS[0].value);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentB64, setAttachmentB64] = useState(null);
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.internalRequestListMine();
      setItems(r?.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Contacter l\'admin · Code 2 Roues';
    refresh();
  }, [refresh]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop lourd (max 5 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(',')[1] || '';
      setAttachment({ filename: f.name, mime_type: f.type, size: f.size });
      setAttachmentB64(b64);
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) { toast.error('Sujet trop court'); return; }
    if (message.trim().length < 5) { toast.error('Message trop court'); return; }
    setSending(true);
    try {
      const payload = { subject: subject.trim(), category, message: message.trim() };
      if (attachment && attachmentB64) {
        payload.attachment = { ...attachment, data_b64: attachmentB64 };
      }
      await api.internalRequestCreate(payload);
      toast.success('Demande envoyée à l\'admin');
      setSubject(''); setCategory(CATS[0].value); setMessage(''); setAttachment(null); setAttachmentB64(null);
      refresh();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="contact-admin-page">
      <div className={`text-[10.5px] font-black uppercase tracking-[0.22em] text-${accent}-500 mb-2`}>Contacter l&apos;admin</div>
      <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Une question ?
      </h1>
      <p className="text-sm text-white/60 mt-2 mb-6">
        Envoie une demande directement à l&apos;équipe admin. Tu recevras une notification par email dès qu&apos;on te répond.
      </p>

      {/* Formulaire */}
      <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-6 space-y-3" data-testid="contact-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Catégorie</span>
            <select value={category} onChange={e => setCategory(e.target.value)} className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="contact-category">
              {CATS.map(c => <option key={c.value} value={c.value} className="bg-[#0d0d0e]">{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Sujet *</span>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} placeholder="Résumé court" className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="contact-subject" />
          </label>
        </div>
        <label className="block">
          <span className="text-[11px] font-black uppercase tracking-widest text-white/60">Message *</span>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} maxLength={5000} placeholder="Décris précisément ta demande…" className="mt-1 w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-white text-sm" data-testid="contact-message" />
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-white/75 hover:bg-white/5 text-xs font-semibold" data-testid="contact-attach-btn">
            <Paperclip size={12} /> Joindre un fichier
            <input type="file" onChange={onFile} className="hidden" accept="image/*,application/pdf" />
          </label>
          {attachment && (
            <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
              <Paperclip size={12} /> {attachment.filename} ({Math.round(attachment.size / 1024)} Ko)
              <button type="button" onClick={() => { setAttachment(null); setAttachmentB64(null); }} className="ml-1 text-white/40 hover:text-white"><X size={12} /></button>
            </div>
          )}
          <div className="flex-1" />
          <button type="submit" disabled={sending} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-black shadow-lg shadow-rose-900/30 transition disabled:opacity-60" data-testid="contact-submit">
            <Send size={13} /> {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </form>

      {/* Historique */}
      <h2 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mes demandes</h2>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-6 text-center">
          <Mail className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-white/60 text-sm">Aucune demande pour le moment.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="contact-history">
          {items.map(r => (
            <details key={r.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`request-${r.id}`}>
              <summary className="cursor-pointer flex items-center gap-3 p-4 hover:bg-white/5">
                <StatusIcon status={r.status} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{r.subject}</div>
                  <div className="text-white/50 text-xs mt-0.5">
                    {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} · <span className="capitalize">{r.category.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </summary>
              <div className="px-4 pb-4 space-y-3 text-sm">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Ton message</div>
                  <div className="text-white/85 whitespace-pre-wrap">{r.message}</div>
                </div>
                {r.admin_reply && (
                  <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] p-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1">Réponse de l&apos;admin</div>
                    <div className="text-emerald-100/90 whitespace-pre-wrap">{r.admin_reply}</div>
                    {r.admin_reply_at && <div className="text-emerald-400/60 text-[11px] mt-2">Répondu le {new Date(r.admin_reply_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</div>}
                  </div>
                )}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === 'resolved') return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (status === 'in_progress') return <Clock className="w-4 h-4 text-amber-400 shrink-0" />;
  return <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />;
}

function StatusBadge({ status }) {
  const map = {
    new: { label: 'Nouveau', c: 'text-rose-300 bg-rose-500/15 border-rose-500/25' },
    in_progress: { label: 'En cours', c: 'text-amber-300 bg-amber-500/15 border-amber-500/25' },
    resolved: { label: 'Résolu', c: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/25' },
    archived: { label: 'Archivé', c: 'text-white/50 bg-white/5 border-white/10' },
  };
  const s = map[status] || map.new;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-black uppercase tracking-widest ${s.c}`}>{s.label}</span>;
}
